import { RefObject, useLayoutEffect, useState } from 'react';

/**
 * Extract innerText from a DOM element referenced by a ref.
 * Updates when the ref changes or when the element's content changes.
 * @param ref - Ref object pointing to an HTMLElement (can be null)
 * @returns The text content of the element, or null if empty string or not available
 */
export const useInnerText = <T extends HTMLElement>(
  ref: RefObject<T | null>,
  options?: { observe: boolean },
): string | null => {
  const { observe = false } = options ?? {};
  const [innerText, setInnerText] = useState<string | null>(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return setInnerText(null);

    let active = true;
    const updateInnerText = () => {
      // A queued mutation can arrive after unmount, once the observer is gone.
      if (!active) return;
      const next = element.textContent?.trim() || null;
      setInnerText((current) => (current === next ? current : next));
    };

    updateInnerText();

    if (!observe) {
      return () => {
        active = false;
      };
    }
    const observer = new MutationObserver(updateInnerText);
    observer.observe(element, observeOptions);

    return () => {
      active = false;
      observer.disconnect();
    };
  }, [ref, observe]);

  return innerText;
};

const observeOptions: MutationObserverInit = {
  characterData: true,
  childList: true,
  subtree: true,
};
